import { isSupportedMobileUiAction } from "./uiConfig";
import { publicProfileUrl } from "./accountProfile";
import type { MobileStore } from "./state";
import type { MobileProfileView, MobileTab, MobileUiActionName, NativeBridge, ProviderConnectionActionName } from "./types";

export interface MobileActionContext {
  store: MobileStore;
  nativeBridge: NativeBridge;
}

type MobileActionHandler = (control: HTMLElement, context: MobileActionContext) => void;

function mobileTab(value: unknown): MobileTab | null {
  const tab = String(value || "");
  return ["home", "inbox", "olivia", "create", "profile"].includes(tab) ? tab as MobileTab : null;
}

function profileView(value: unknown): MobileProfileView | null {
  const view = String(value || "");
  return ["public", "hub", "account", "ai-manager", "ai-customize", "ai-checkins", "ai-messaging", "ai-memory", "ai-connections"].includes(view)
    ? view as MobileProfileView
    : null;
}

function numericIndex(value: unknown) {
  const index = Number(value);
  return Number.isInteger(index) && index >= 0 ? index : null;
}

function confirmControl(control: HTMLElement, fallback: string) {
  const message = control.dataset.confirmation || fallback;
  if (!message) return true;
  return typeof window === "undefined" || window.confirm(message) === true;
}

export const MOBILE_ACTION_HANDLERS: Record<MobileUiActionName, MobileActionHandler> = {
  navigate(control, { store, nativeBridge }) {
    const tab = mobileTab(control.dataset.tab);
    if (!tab) return;
    void nativeBridge.impact();
    store.selectTab(tab);
  },
  "open-conversation"(control, { store, nativeBridge }) {
    const reference = String(control.dataset.conversationRef || "").trim();
    if (!reference) return;
    void nativeBridge.impact();
    store.selectTab("inbox");
    void store.openConversation(reference);
  },
  "back-to-inbox"(_control, { store, nativeBridge }) {
    void nativeBridge.impact();
    store.selectTab("inbox");
    store.closeConversation();
  },
  "refresh-inbox"(_control, { store }) {
    void store.refreshLiveInbox();
  },
  "retry-session"(_control, { store }) {
    void store.loadSession();
  },
  "retry-inbox"(_control, { store }) {
    void store.loadInbox();
  },
  "retry-conversation"(_control, { store }) {
    const reference = store.getState().inbox.selectedConversation?.conversationRef;
    if (reference) void store.openConversation(reference);
  },
  "load-more-conversations"(_control, { store }) {
    void store.loadInbox(store.getState().inbox.page?.nextCursor || null);
  },
  "load-earlier-messages"(_control, { store }) {
    void store.loadEarlierMessages();
  },
  "toggle-inbox-filters"(_control, { store, nativeBridge }) {
    void nativeBridge.impact();
    store.toggleInboxFilters();
  },
  "close-inbox-filters"(_control, { store }) {
    store.toggleInboxFilters(false);
  },
  "reset-inbox-filters"(_control, { store }) {
    void store.resetInboxFilters();
  },
  "retry-inbox-metadata"(_control, { store }) {
    void store.loadInboxMetadata();
  },
  "retry-wctek"(_control, { store }) {
    void store.loadWctekWorkspace();
  },
  "retry-wctek-history"(_control, { store }) {
    void store.loadWctekSessions();
  },
  "new-wctek-session"(control, { store, nativeBridge }) {
    if (store.hasUnsentWctekDraft() && !confirmControl(control, "Discard this unsent WCTEK AI draft and start a new conversation?")) return;
    void nativeBridge.impact();
    void store.startNewWctekSession();
  },
  "open-wctek-session"(control, { store, nativeBridge }) {
    const sessionId = String(control.dataset.wctekSessionId || "").trim();
    if (!sessionId) return;
    if (store.hasUnsentWctekDraft() && !confirmControl(control, "Discard this unsent WCTEK AI draft and open another conversation?")) return;
    void nativeBridge.impact();
    void store.openWctekSession(sessionId);
  },
  "retry-wctek-send"(_control, { store }) {
    void store.sendWctekMessage();
  },
  "cancel-wctek-send"(_control, { store }) {
    store.cancelWctekSend();
  },
  "retry-companion-settings"(_control, { store }) {
    void store.loadCompanionSettings();
  },
  "open-profile-view"(control, { store, nativeBridge }) {
    const view = profileView(control.dataset.profileView);
    if (!view) return;
    void nativeBridge.impact();
    store.openProfileView(view);
  },
  "retry-social-links"(control, { store }) {
    if (store.getState().socialLinks.dirty && !confirmControl(control, "Discard unsaved profile links and reload?")) return;
    void store.loadSocialLinks({ force: true });
  },
  "retry-public-profile"(_control, { store }) {
    void store.loadPublicProfile();
  },
  "open-public-profile"(_control, { store, nativeBridge }) {
    const url = publicProfileUrl(store.getState().activeAccount?.username || "");
    if (url) void nativeBridge.openProfileLink(url);
  },
  "add-social-link"(_control, { store, nativeBridge }) {
    void nativeBridge.impact();
    store.addSocialLink();
  },
  "move-social-link"(control, { store }) {
    const index = numericIndex(control.dataset.socialIndex);
    const direction = control.dataset.direction === "up" ? -1 : control.dataset.direction === "down" ? 1 : 0;
    if (index === null || !direction) return;
    store.moveSocialLink(index, direction);
  },
  "remove-social-link"(control, { store }) {
    const index = numericIndex(control.dataset.socialIndex);
    if (index !== null) store.removeSocialLink(index);
  },
  "retry-checkins"(_control, { store }) {
    void store.loadCheckinSettings({ force: true });
  },
  "use-device-timezone"(_control, { store }) {
    store.useDeviceTimezone();
  },
  "reset-checkins"(control, { store }) {
    if (!confirmControl(control, "Reset check-ins to their defaults?")) return;
    void store.resetCheckinSettings();
  },
  "retry-review-drafts"(_control, { store }) {
    void store.loadReviewDrafts();
  },
  "select-review-draft"(control, { store, nativeBridge }) {
    const draftId = String(control.dataset.draftId || "").trim();
    if (!draftId) return;
    void nativeBridge.impact();
    store.selectReviewDraft(draftId);
  },
  "reject-review-draft"(control, { store }) {
    if (!confirmControl(control, "Reject this draft?")) return;
    void store.markReviewDraft("reject");
  },
  "human-takeover-review-draft"(control, { store }) {
    if (!confirmControl(control, "Mark this conversation for human takeover?")) return;
    void store.markReviewDraft("human_takeover");
  },
  "retry-memory-hydration"(_control, { store }) {
    void store.loadMemoryHydration();
  },
  "retry-provider-connections"(_control, { store }) {
    void store.loadProviderConnections();
  },
  "execute-provider-action"(control, { store, nativeBridge }) {
    const targetRef = String(control.dataset.providerRef || "").trim();
    const action = String(control.dataset.providerAction || "") as ProviderConnectionActionName;
    if (!targetRef || !["connect", "reconnect", "recheck", "verify-subscription", "disconnect"].includes(action)) return;
    if (!confirmControl(control, "")) return;
    void nativeBridge.impact();
    void store.executeProviderAction(targetRef, action).then((path) => {
      if (path) void nativeBridge.openSystemPath(path);
    });
  },
  "open-auth-security"(_control, { nativeBridge }) {
    void nativeBridge.impact();
    void nativeBridge.openSystemPath("/login/security.html");
  },
  logout(_control, { store, nativeBridge }) {
    const state = store.getState();
    const hasUnsavedProfileChanges = typeof store.hasUnsavedProfileChanges === "function"
      ? store.hasUnsavedProfileChanges()
      : state.companion.dirty;
    const hasWctekDraft = typeof store.hasUnsentWctekDraft === "function" && store.hasUnsentWctekDraft();
    const hasInboxDraft = typeof store.hasUnsentInboxDraft === "function" && store.hasUnsentInboxDraft();
    if (hasUnsavedProfileChanges || hasWctekDraft || hasInboxDraft) {
      const component = hasWctekDraft
        ? state.ui.screens.olivia.components.find((item) => item.kind === "wctek-chat")
        : hasInboxDraft
          ? state.ui.screens.inbox.components.find((item) => item.kind === "conversation-detail")
        : state.ui.screens.profile.components.find((item) => item.kind === "companion-settings");
      const message = hasWctekDraft
        ? component?.labels?.discardLogout || "Discard this unsent WCTEK AI draft and sign out?"
        : hasInboxDraft
          ? component?.labels?.discardLogout || "Discard this unsaved Inbox draft and sign out?"
        : component?.labels?.unsavedLogout || "Discard unsaved profile changes and sign out?";
      if (typeof window !== "undefined" && window.confirm(message) !== true) return;
    }
    void Promise.resolve(store.logout()).finally(() => {
      if (typeof nativeBridge.clearSession === "function") void nativeBridge.clearSession();
    });
  }
};

export function dispatchMobileAction(action: unknown, control: HTMLElement, context: MobileActionContext) {
  if (!isSupportedMobileUiAction(action)) return false;
  MOBILE_ACTION_HANDLERS[action](control, context);
  return true;
}
